import React, { useContext, useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Input, message, Radio, Row, Spin } from 'antd';
// import ReCAPTCHA from 'react-google-recaptcha';
import InputRut from '../../../components/form/inputs/InputRut';
import PepInfo from './PepInfo';
import AuthService from '../AuthService';
import { rutValidator } from '../../../utils/validators';
import { getRutNumericValue } from '../../../utils/rut';
import EmailVerificationModal from './EmailVerificationModal';
import tokens from '../../../utils/tokens';
import AloneCol from '../../../components/common/AloneCol';
import formCol from '../../../utils/formCol';
import useCountdown from '../../../hooks/useCountdown';
import form from '../../../utils/form';
import { EnrollmentFormContext } from '../../../providers/EnrollmentFormContextProvider';

const ContactForm = () => {
  const [isLoading, setLoading] = useState(false);
  const [isResending, setResending] = useState(false);
  const [isResendEnabled, setResendEnabled] = useState(true);
  const [isVerifyingEmail, setVerifyingEmail] = useState(false);
  const [verificationToken, setVerificationToken] = useState();
  const [hasExpired, setExpired] = useState(false);

  const handleExpiration = useCallback(() => {
    setExpired(true);
  }, [setExpired]);

  const [verificationCountdown, restartCountdown] = useCountdown(300, handleExpiration);
  const { enrollmentForm } = useContext(EnrollmentFormContext);
  // const recaptchaRef = useRef();

  useEffect(() => {
    if (isVerifyingEmail) {
      restartCountdown();
    }
  }, [isVerifyingEmail, restartCountdown]);

  const handleSubmit = async (values, isResend) => {
    setLoading(true);

    // const recaptcha = await recaptchaRef.current.executeAsync();
    const rut = getRutNumericValue(values.person.rut);
    const dv = values.person.rut.split('-')[1];

    const payload = {
      ...values.person,
      rut,
      dv,
      recaptcha: 'hola',
      emailValidation: undefined,
    };

    AuthService.getAuth(payload)
      .then(({ data }) => {
        setExpired(false);
        setVerificationToken(data.token);
        setVerifyingEmail(true);
        restartCountdown();
        if (isResend) {
          message.success('Código reenviado');
          setResendEnabled(false);
          setTimeout(() => setResendEnabled(true), 10000);
        }
      })
      .catch((err) => {
        message.error(err.data?.message || 'Ocurrió un error. Intente nuevamente.');
      })
      .finally(() => {
        setLoading(false);
        setResending(false);
      });
  };

  const resendForm = () => {
    setResending(true);
    handleSubmit(enrollmentForm.getFieldsValue(), true);
  };

  const cancelEmailVerification = () => setVerifyingEmail(false);

  const preventCopyPasteAndSpaces = (e) => {
    if (e.type === 'paste') {
      e.preventDefault();
      return;
    }

    // Prevent spaces
    const { value } = e.target;
    if (/\s/g.test(value)) {
      e.target.value = value.replace(/\s/g, '');
    }
  };

  useEffect(() => {
    enrollmentForm.setFieldsValue({ person: { hasAcceptedTerms: true } });
  }, []);

  return (
    <Row justify="center">
      <Col {...formCol.responsive}>
        <Spin spinning={isLoading && !isResending}>
          <Form
            form={enrollmentForm}
            layout="vertical"
            onFinish={handleSubmit}
            validateMessages={form.validateMessages}
          >
            <p>
              <b>Ingresa tus datos para comenzar</b>
            </p>
            <Row gutter={tokens.forms.horizontalGutterInPx}>
              <AloneCol span={12}>
                <Form.Item
                  name={['person', 'name']}
                  label="Nombre"
                  required
                  rules={[{ required: true }]}
                >
                  <Input type="text" maxLength={25} />
                </Form.Item>
                <Form.Item name={['person', 'hasAcceptedTerms']} valuePropName="checked" preserve />
              </AloneCol>
              <Col span={12}>
                <Form.Item
                  name={['person', 'firstSurname']}
                  label="Apellido Paterno"
                  rules={[{ required: true }]}
                >
                  <Input type="text" maxLength={25} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['person', 'secondSurname']}
                  label="Apellido Materno"
                  className="not-required"
                  rules={[{ required: false }]}
                >
                  <Input type="text" maxLength={25} />
                </Form.Item>
              </Col>
              <AloneCol span={12}>
                <Form.Item
                  name={['person', 'rut']}
                  label="RUT"
                  required
                  rules={[
                    {
                      required: true,
                      validator: rutValidator,
                    },
                  ]}
                  validateTrigger="onBlur"
                >
                  <InputRut type="text" maxLength={17} />
                </Form.Item>
              </AloneCol>
              <Col span={12}>
                <Form.Item
                  name={['person', 'email']}
                  label="E-mail"
                  rules={[{ required: true, type: 'email' }]}
                >
                  <Input type="email" maxLength={64} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="emailValidation"
                  label="Reescriba su E-mail"
                  className="not-required"
                  validateTrigger="onBlur"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value !== enrollmentForm.getFieldValue(['person', 'email'])) {
                          return Promise.reject(new Error('Los correos no coinciden.'));
                        }
                        return Promise.resolve();
                      },
                      type: 'email',
                    },
                  ]}
                >
                  <Input
                    type="email"
                    maxLength={64}
                    onPaste={preventCopyPasteAndSpaces}
                    onInput={preventCopyPasteAndSpaces}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['person', 'phone']}
                  label="Teléfono Móvil"
                  rules={[
                    {
                      required: true,
                      len: 9,
                    },
                  ]}
                  normalize={form.normalizers.numeric}
                >
                  <Input type="text" maxLength={9} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['person', 'phoneAlt']}
                  label="Teléfono Fijo (opcional)"
                  className="not-required"
                  rules={[
                    {
                      len: 9,
                    },
                  ]}
                  normalize={form.normalizers.numeric}
                >
                  <Input type="text" maxLength={9} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="¿Eres una Persona Expuesta Políticamente?"
                  name={['person', 'isPep']}
                  rules={[{ required: true, message: 'Seleccione una opción' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Radio.Group>
                    <Radio value>Sí</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <PepInfo />
              </Col>
              <Col span={24}>
                <Form.Item style={{ marginTop: '1rem' }}>
                  <Form.Item
                    label="¿Eres cónyuge o pariente de una Persona Expuesta Políticamente hasta el segundo grado de consanguinidad?"
                    name={['person', 'hasPepRelative']}
                    rules={[{ required: true, message: 'Seleccione una opción' }]}
                  >
                    <Radio.Group>
                      <Radio value>Sí</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item>
                  <Button type="primary" htmlType="submit" block>
                    Continuar
                  </Button>
                </Form.Item>
              </Col>

              {/* <ReCAPTCHA
                size="invisible"
                badge="bottomleft"
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_PUBLIC_KEY}
              /> */}
            </Row>
          </Form>
          <EmailVerificationModal
            onCancel={cancelEmailVerification}
            isVisible={isVerifyingEmail}
            token={verificationToken}
            isResending={isResending}
            resend={resendForm}
            timeLeft={verificationCountdown}
            hasExpired={hasExpired}
            isResendEnabled={isResendEnabled}
          />
        </Spin>
      </Col>
    </Row>
  );
};

export default ContactForm;
